import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, LabelList, XAxis } from 'recharts';
import { cn } from 'utils/cn';
import { formatCompact } from './figureFormat';
import {
    FigureLink,
    Headline,
    KpiFormProps,
    RowLabel,
    rowLink,
    ShareBar,
    SIDE_CLASS,
    TargetStrip,
    topAndRest,
    VALUE_CLASS,
} from './KpiParts';

/** The rows of the one-column size before everything else becomes one row, and the columns of the wide one. */
const TOP_ROWS = 4;
const MAX_COLUMNS = 8;

/** The space between two columns: the chart leaves it between its bars, and each name pads itself by half of it. */
const COLUMN_GAP = 12;

/**
 * A breakdown as bars. One column wide: the top values and the rest as rows, each a link, under the total or the
 * target line; a row's name keeps 64px beside its value in full, and a value that leaves it less is written short. Two columns wide: the total beside one column per value, the bar and the name under it both opening
 * that value's records.
 */
export function BreakdownBars({ name, definition, figure, size, links, format }: KpiFormProps) {
    const navigate = useNavigate();
    const intl = useIntl();
    const total = (
        <Headline
            key="total"
            value={figure.value}
            format={format}
            to={links?.list}
            className="text-foreground font-semibold"
        />
    );

    if (size !== '2x2') {
        const rows = topAndRest(figure, TOP_ROWS);
        const largest = Math.max(...rows.map((row) => row.value));
        return (
            <>
                {definition.target ? (
                    <TargetStrip figure={figure} target={definition.target} format={format} lead={total} />
                ) : (
                    <span className="text-meta text-muted-foreground truncate">
                        <FormattedMessage id="home.widgets.total" values={{ value: total }} />
                    </span>
                )}
                <ul className="flex min-h-0 flex-1 flex-col justify-between gap-1 overflow-y-auto">
                    {rows.map((row) => (
                        <li key={row.key ?? (row.isRest ? 'rest' : 'none')}>
                            <FigureLink
                                to={rowLink(links, row)}
                                className="text-meta grid grid-cols-[minmax(4rem,1fr)_auto] gap-x-2 gap-y-0.5 has-[[data-short]]:grid-cols-[minmax(0,1fr)_auto]"
                            >
                                <span className="truncate">
                                    <RowLabel row={row} />
                                </span>
                                <Headline value={row.value} format={format} className={VALUE_CLASS} />
                                <ShareBar
                                    share={row.value / largest}
                                    className="col-span-full"
                                    trackClassName="bg-divider-soft"
                                />
                            </FigureLink>
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    const columns = (figure.rows ?? []).slice(0, MAX_COLUMNS).map((row) => ({
        ...row,
        name: row.label ?? intl.formatMessage({ id: 'home.widgets.no-value' }),
    }));
    return (
        <div className="flex min-h-0 flex-1 gap-6">
            {/* Narrower than beside a line: a total wider than this is written short, and the bars keep their names. */}
            <div className={cn(SIDE_CLASS, 'max-w-[40%]')}>
                {/* The figure, then the words after it on a line of their own. */}
                <span className="text-muted-foreground text-caption flex flex-col gap-1.5">
                    <FormattedMessage
                        id="home.widgets.total"
                        values={{
                            value: <Headline key="total" value={figure.value} format={format} to={links?.list} />,
                        }}
                    />
                </span>
                {definition.target ? (
                    <TargetStrip figure={figure} target={definition.target} format={format} className="mt-2" />
                ) : null}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <ChartContainer config={{}} role="img" aria-label={name} className="aspect-auto min-h-0 w-full flex-1">
                    <BarChart
                        data={columns}
                        accessibilityLayer={false}
                        barCategoryGap={COLUMN_GAP}
                        margin={{ top: 20, right: 0, bottom: 0, left: 0 }}
                    >
                        <XAxis dataKey="name" hide />
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    hideIndicator
                                    formatter={(value) => (
                                        <span className="text-foreground font-medium tabular-nums">
                                            {format(Number(value))}
                                        </span>
                                    )}
                                />
                            }
                        />
                        <Bar
                            dataKey="value"
                            fill="var(--color-primary)"
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={false}
                            className={links ? 'cursor-pointer' : undefined}
                            onClick={(_, index) => {
                                const to = links?.row(columns[index]);
                                if (to) navigate(to);
                            }}
                        >
                            {/* Short, so that eight of them stand side by side; the tooltip has the value in full. */}
                            <LabelList
                                dataKey="value"
                                position="top"
                                formatter={(value) => formatCompact(Number(value))}
                                className="fill-foreground text-caption tabular-nums"
                            />
                        </Bar>
                    </BarChart>
                </ChartContainer>
                {/* One name per bar, each as wide as the bar's band in the chart above. */}
                <ul className="flex">
                    {columns.map((row) => (
                        <li key={row.key ?? 'none'} className="min-w-0 flex-1 px-1.5 text-center">
                            <FigureLink
                                to={links?.row(row)}
                                className="text-foreground-secondary text-caption line-clamp-2 h-7 leading-[1.17] break-words"
                            >
                                {row.name}
                            </FigureLink>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
